"use client";

import Link from "next/link";
import { ArrowRight, Clock3 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getCategoryById, toolIcons } from "@/lib/tools/registry";
import { cn } from "@/lib/utils";
import type { ToolRegistryItem } from "@/types/tools";
import { FavoriteButton } from "./favorite-button";
import { PrivacyBadge } from "./privacy-badge";

type ToolCardProps = {
  tool: ToolRegistryItem;
  compact?: boolean;
};

export function ToolCard({ tool, compact = false }: ToolCardProps) {
  const Icon = toolIcons[tool.id];
  const category = getCategoryById(tool.category);

  return (
    <div className="group relative rounded-lg border border-zinc-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-zinc-300 hover:shadow-md">
      <FavoriteButton toolId={tool.id} className="absolute right-3 top-3" />
      <Link href={tool.route} prefetch={false} className="block pr-9">
        <div className="mb-3 flex items-center gap-3">
          <span
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-md text-white",
              category?.accent ?? "bg-zinc-700",
            )}
          >
            <Icon className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-zinc-950">{tool.name}</h3>
            {!compact ? (
              <p className="text-xs text-zinc-500">{category?.name}</p>
            ) : null}
          </div>
        </div>
        {!compact ? (
          <p className="mb-4 line-clamp-2 min-h-10 text-sm leading-5 text-zinc-600">
            {tool.description}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <PrivacyBadge localOnly={tool.localOnly} />
          {tool.status === "coming-soon" ? (
            <Badge variant="warning">
              <Clock3 className="h-3 w-3" />
              Coming soon
            </Badge>
          ) : null}
        </div>
        <div className="mt-4 flex items-center text-xs font-medium text-zinc-600">
          Open tool
          <ArrowRight className="ml-1 h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
        </div>
      </Link>
    </div>
  );
}
